import { of } from "rxjs";
import { map } from "rxjs/operators";
import {
    IBcActiveContest, IBcContest, IBcContestApplication, IBcContestStudentCourse,
    IBcContestStudentData, IBcContestCourse
} from "../../models/BcContest";
import { AppService } from "../../services/app.service";
import { MessageService } from "../../services/message.service";

export interface ICourseCard extends IBcContestStudentCourse {
    groups?: ICourseCard[];
    error?: string;
    selectedGroupId?: string;
}

const store: {
    contest?: IBcContest,
    activeContest?: IBcActiveContest,
    student?: IBcContestStudentData,
    courses?: IBcContestCourse[],
    applications: { [key: string]: IBcContestApplication[] },
    shouldOpenPreferences?: boolean,
    shouldSync?: number,
    messageTemplates?: { pointInfo: string }
} = {
    applications: {}
};

function createApplicationCacheKey(args: ApplicationQuery) {
    let key = `${args.contestId}.`;

    if (args.courseId) key += `c${args.courseId}`;
    if (args.studentId) key += `s${args.studentId}`;

    return key;
}

export function getMessageTemplates(app: AppService, service: MessageService) {
    if (store.messageTemplates) return of(store.messageTemplates);

    return app.addLoading(service.getText('BC_COURSES_POINT_INFO')).pipe(map(data => {
        const values: { [code: string]: string } = {};

        data.forEach(t => {
            values[t.Code] = t.Text;
        });

        store.messageTemplates = {
            pointInfo: values['BC_COURSES_POINT_INFO']
        };

        return store.messageTemplates;
    }));
}

export type ApplicationQuery = {
    contestId: number,
    courseId?: number,
    studentId?: number,
    hasError?: boolean
};

export const bcState = {
    getContest(): IBcContest {
        return store.contest;
    },
    setContest(contest: IBcContest) {
        store.contest = contest;
    },
    getActiveContest(): IBcActiveContest {
        return store.activeContest;
    },
    setActiveContest(contest: IBcActiveContest) {
        store.activeContest = contest;
    },
    getStudentData(): IBcContestStudentData {
        return store.student;
    },
    setStudentData(student: IBcContestStudentData) {
        store.student = student;
    },
    getCourses() {
        return store.courses;
    },
    setCourses(items: IBcContestCourse[]) {
        store.courses = items;
    },
    clearCourses() {
        store.courses = undefined;
    },
    getApplications(args: ApplicationQuery) {
        const key = createApplicationCacheKey(args);
        return store.applications[key];
    },
    setApplications(args: ApplicationQuery, items: IBcContestApplication[]) {
        const key = createApplicationCacheKey(args);
        store.applications[key] = items;
    },
    clearApplications() {
        store.applications = {};
    },
    updateApplication(item: IBcContestApplication) {
        for (const q in store.applications) {
            const cached = store.applications[q].find(t => t.Id == item.Id);
            if (cached) {
                Object.assign(cached, item);
            }
        }
    },
    requestOpenPreferences() {
        store.shouldOpenPreferences = true;
    },
    shouldOpenPreferences() {
        const val = store.shouldOpenPreferences;
        store.shouldOpenPreferences = false;
        return val;
    },
    requestSync(contestId: number) {
        store.shouldSync = contestId;
    },
    shouldSync(contestId: number) {
        if (store.shouldSync == contestId) {
            store.shouldSync = undefined;
            return true;
        }

        return false;
    }
};

export const helpers = {
    groupCourses(data: IBcContestStudentCourse[]): {
        courses: ICourseCard[],
        examinationTypes: string[],
        languages: string[]
    } {
        const courses: ICourseCard[] = [];
        const examinationTypes: string[] = [];
        const languages: string[] = [];

        const groups: { [id: number]: ICourseCard[] } = {};

        data.forEach(t => {
            const item: ICourseCard = { ...t };

            if (!groups[t.Number]) {
                groups[t.Number] = [];
            }

            groups[t.Number].push(item);

            item.groups = groups[t.Number];

            if (groups[t.Number].length == 1) {
                courses.push(item);
            }

            if (item.ExaminationType && !examinationTypes.includes(item.ExaminationType)) {
                examinationTypes.push(item.ExaminationType);
            }

            if (item.Language && !languages.includes(item.Language)) {
                languages.push(item.Language);
            }
        });

        return {
            courses,
            languages,
            examinationTypes
        };
    }
};
